import { createPdfDocumentSession, PdfDocumentSession, clampPdfPage } from "./document-session";
import useMediaQuery from "../../utils/hooks/use-media-query";
import MobilePdfPage from "./mobile-page";
import { useTranslation as useInterfaceTranslation } from "react-i18next";
import React, { forwardRef, useEffect, useMemo, useState, useRef, useCallback } from "react";
import "./index.styles.scss";
import { jsPDF } from "jspdf";
import { Spinner } from "react-bootstrap";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchSteelTypes } from "Redux/middlwares/steelType";
import checkIcon from "../../assets/img/icon/check.svg";
import { useTranslation } from "react-i18next";
import PreviewRebarTable from "./rebars";
import PdfOrderSummary from "./summary";
import PreviewStandardNets from "./nets/StandardNet";
import PreviewSpecialNets from "./nets/SpecialNet";
import PreviewPileTable from "./pile";
import toast from "react-hot-toast";
import { OrderPdfType } from "Constants/order";

const PdfPreview = forwardRef(
  (
    {
      setDownloadFlag,
      downloadFlag,
      setGeneratePdfFlag,
      generatePdfFlag,
      setPdfFile,
      pdfFile,
      printCanvas,
      setPrintCanvas,
      setLoader,
      loader,
      setOrderPdfDownload,
      orderPdfDownload,
      onHide,
      order,
      rebars,
      nets,
      piles,
      headers,
      priceShow,
      setPdfType,
      pdfType,
      bidFees,
      pdfLanguage,
      feeType,
    }: any,
    ref
  ) => {
  const { t: uiT } = useInterfaceTranslation("interface");
    const pageSize = 1400;
    const mobile = useMediaQuery("(max-width: 991.98px)");
    const [documentSession, setDocumentSession] = useState<PdfDocumentSession | null>(null);
    const [documentError, setDocumentError] = useState(false);
    const [documentRetry, setDocumentRetry] = useState(0);
    const captureScale = useRef(mobile ? 1.5 : 2);
    const { i18n, t } = useTranslation(["common", "warning"]);
    const dispatch = useAppDispatch();
    const steelTypes = useAppSelector((state) => state.steelType.steelTypes);

    const stylingRef = useRef<HTMLDivElement>();
    const previewViewportRef = useRef<HTMLDivElement>();
    const [previewScale, setPreviewScale] = useState(1);
    const [previewHeight, setPreviewHeight] = useState(pageSize);
    const [currentPreviewPage, setCurrentPreviewPage] = useState(0);
    const generating = useRef(false);

    const [rebarsRefs, setRebarsRef] = useState();
    const [standardNetRefs, setStandardNetsRefs] = useState();
    const [specialNetsRef, setSpecialNetsRef] = useState();
    const [pilesRef, setPilesRef] = useState();
    const [summaryRef, setSummaryRef] = useState();
    const [standardNetTran, setStandardNetTran] = useState({
      units: 0,
      weight: 0,
    });

    const [rebarsTotalPrice, setRebarsTotalPrice] = useState(0);
    const [standardNetsTotalPrice, setStandardNetsTotalPrice] = useState(0);
    const [specialNetsTotalPrice, setSpecialNetsTotalPrice] = useState(0);
    const customRightToLeft = pdfLanguage ? pdfLanguage === "he" : i18n.language.startsWith("he");
    const languageReady = i18n.isInitialized;
    const usesDedicatedSummary = pdfType === OrderPdfType.ORIGINAL_PDF;

    const totalPages = useMemo(() => {
      let total = {};
      if (rebarsRefs && (rebarsRefs as any).current)
        total = {
          rebars: (rebarsRefs as any).current.length,
        };

      if (standardNetRefs && (standardNetRefs as any).current)
        total = {
          ...total,
          standardNets: (standardNetRefs as any).current.length,
        };

      if (specialNetsRef && (specialNetsRef as any).current)
        total = {
          ...total,
          specialNets: (specialNetsRef as any).current.length,
        };

      if (pilesRef && (pilesRef as any).current)
        total = {
          ...total,
          piles: (pilesRef as any).current.length,
        };

        if (summaryRef && (summaryRef as any).current) {
        total = { 
          ...total,
          summary: (summaryRef as any).current.length,
        };
      }

      return total;
    }, [rebarsRefs, standardNetRefs, specialNetsRef, pilesRef, summaryRef]);

    const totalPageCount = useMemo(
      () =>
        Object.values(totalPages).reduce(
          (sum: number, count: any) => sum + Number(count || 0),
          0
        ),
      [totalPages]
    );

    const pageCount = documentSession?.pages.length || Number(totalPageCount) || 1;
    const visiblePage = clampPdfPage(currentPreviewPage, pageCount);
    const previewPage = documentSession?.pages[visiblePage] || null;
    useEffect(() => {
      if (!languageReady || !totalPageCount || !stylingRef.current) return;
      let active = true;
      let session: PdfDocumentSession | undefined;
      setCurrentPreviewPage(0); setDocumentError(false); setDocumentSession(null);
      createPdfDocumentSession(stylingRef.current, captureScale.current).then(result => {
        session = result;
        if (active) setDocumentSession(result); else result.dispose();
      }).catch(() => { if (active) setDocumentError(true); });
      return () => { active = false; session?.dispose(); };
      // A preview owns a single snapshot after its section refs are registered.
    }, [languageReady, totalPageCount, documentRetry]);

    useEffect(() => {
      const viewport = previewViewportRef.current;
      const documentRoot = stylingRef.current;
      if (!viewport || !documentRoot) return;

      const updatePreviewSize = () => {
        const availableWidth = Math.max(0, viewport.clientWidth - 16);
        const scale = Math.min(1, availableWidth / 940);
        setPreviewScale(scale);
        setPreviewHeight(documentRoot.scrollHeight * scale);
      };

      updatePreviewSize();
      const observer = new ResizeObserver(updatePreviewSize);
      observer.observe(viewport);
      observer.observe(documentRoot);
      return () => observer.disconnect();
    }, [totalPageCount, languageReady]);

    const navigatePreview = (direction: number) => {
      setCurrentPreviewPage(page => clampPdfPage(page + direction, pageCount));
      if (!mobile) {
        const pages = Array.from(stylingRef.current?.querySelectorAll<HTMLElement>(".pdf-document-page") || []);
        previewViewportRef.current?.scrollTo({ top: (pages[clampPdfPage(currentPreviewPage + direction, pageCount)]?.offsetTop || 0) * previewScale, behavior: "smooth" });
      }
    };

    useEffect(() => {
      steelTypes.length === 0 && dispatch(fetchSteelTypes());
    }, [steelTypes, dispatch]);

    useEffect(() => {
      const hasElements = (ref) => ref?.current?.length > 0;

      const refsHaveElements =
        hasElements(rebarsRefs) ||
        hasElements(standardNetRefs) ||
        hasElements(specialNetsRef) ||
        hasElements(pilesRef) ||
        hasElements(summaryRef);

      if (refsHaveElements && languageReady && documentSession) {
        if (downloadFlag || orderPdfDownload) {
          setLoader(true);
          print(true);
        } else if (generatePdfFlag) {
          setLoader(true);
          setTimeout(() => {
            print(false);
          }, 500);
        } else if (printCanvas) {
          setLoader(true);
          setTimeout(() => {
            openPrintCanvas();
          }, 500);
        }
      }
    }, [
      rebarsRefs,
      standardNetRefs,
      specialNetsRef,
      pilesRef,
      summaryRef,
      generatePdfFlag,
      downloadFlag,
      printCanvas,
      languageReady,
      documentSession,
    ]);

    const getHtmlToCanvas = useCallback((page: HTMLElement, signal?: AbortSignal) => {
      if (!documentSession) return Promise.reject(new Error("PDF document is not ready"));
      return documentSession.capture(page, signal);
    }, [documentSession]);
    function openPrintCanvas() {
      const canvases = generateCanvases();
      let windowContent = "";
      let dataUrl;

      Promise.all(canvases)
        .then((values) => {
          values.forEach((canvas: any) => {
            dataUrl = canvas.toDataURL();
            windowContent += "<div style={break-after:page}>";
            windowContent += `<img src=${dataUrl} style="width: 100%; height: auto"/>`;
            windowContent += "</div>";
          });
          const printWin = window.open(
            "about:blank",
            "EasyBarPrintPreview",
            "top=40,left=40,height=700,width=1000,focused=false,resizable=no|0,scrollbars=no,location=no|0"
          );

          if (printWin) {
            // @ts-ignore: Object is possibly 'null'.
            printWin.document.open();
            // @ts-ignore: Object is possibly 'null'.
            printWin.document.write(`
              <!DOCTYPE html>
              <html>
                <head>
                  <meta name="viewport" content="width=device-width, initial-scale=1.0">
                  <title></title>
                </head>
                <body style="width:100%;margin:0 auto;">
                ${windowContent}
                <script>
                  function triggerPrint(event) {
                    window.removeEventListener('load', triggerPrint, false);
                    setTimeout(function() {
                    closeWindow(window.print());
                    }, 100);
                  }
                  function closeWindow(){
                    window.close();
                  }
                  window.addEventListener('load', triggerPrint, false);
                </script>
                </body>
              </html>
          `);
            // @ts-ignore: Object is possibly 'null'.
            printWin.document.close();
          } else {
            toast.error(t("Unable to open print window."));
          }
          setLoader(false);
          onHide();
          setPrintCanvas();
        })
        .catch((err) => {
          toast.error(t("Error opening print preview."));
          console.error("Print Canvas Error:", err);
        });
    }

    function capturePages(): readonly HTMLElement[] { return documentSession?.pages || []; }
    function generateCanvases() { return capturePages().map(page => getHtmlToCanvas(page)); }

    async function print(checkIfDownload: boolean) {
      if (generating.current) return;
      generating.current = true;
      try {
        if (!mobile) document.body.classList.add("not-click");
        setLoader(true);

        const pages = capturePages();
        if (!pages.length) throw new Error("No PDF pages available");
        const pdf = new jsPDF("p", "pt", "a4", true);

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        let sharedScale = 0;
        let offsetX = 0;
        let offsetY = 0;
        for (let index = 0; index < pages.length; index++) {
          const canvas = await getHtmlToCanvas(pages[index]);
          if (index === 0) {
            sharedScale = Math.min(pageWidth / canvas.width, pageHeight / canvas.height);
            offsetX = (pageWidth - canvas.width * sharedScale) / 2;
            offsetY = (pageHeight - canvas.height * sharedScale) / 2;
          }
          const imageWidth = canvas.width * sharedScale;
          const imageHeight = canvas.height * sharedScale;
          const imgData = canvas.toDataURL("image/png");

          pdf.addImage(
            imgData,
            "PNG",
            offsetX,
            offsetY,
            imageWidth,
            imageHeight,
            "",
            "FAST"
          );
          canvas.width = 0; canvas.height = 0;
          if (index < pages.length - 1) pdf.addPage();
        }

        if (checkIfDownload) {
          pdf.save(`easybar_${order.order_no}.pdf`);
          onHide();

          if (downloadFlag) {
            setDownloadFlag();
          } else if (orderPdfDownload) {
            setOrderPdfDownload();
          }

          toast(t("download alert", { ns: "warning" }), {
            icon: <img src={checkIcon} alt="alert icon" />,
          });
        } else {
          const blob = pdf.output("blob");
          setPdfFile(blob);
          setGeneratePdfFlag();
        }
      } catch (error) {
        toast.error(t("Error during PDF generation. Please try again."));
        console.error("Error generating PDF:", error);
      } finally {
        generating.current = false;
        document.body.classList.remove("not-click");
        setLoader(false);
      }
    }

    // handlers
    const handleRebarsRefs = (refs: any) => {
      setRebarsRef(refs);
    };
    const handleStandardNetRefs = (refs: any) => {
      setStandardNetsRefs(refs);
    };
    const handleSpecialdNetRefs = (refs: any) => {
      setSpecialNetsRef(refs);
    };
    const hanldePileRefs = (refs: any) => {
      setPilesRef(refs);
    };

    const handleSummaryRefs = (refs: any) => {
  setSummaryRef(refs);
};

    // Filters
const orderedNets = useMemo(() => {
  return [...(nets || [])].sort(
    (firstNet: any, secondNet: any) =>
      (firstNet.row_number || 0) -
      (secondNet.row_number || 0)
  );
}, [nets]);

const standardNets = useMemo(
  () =>
    orderedNets.filter(
      (item: any) =>
        item.tool.standard_net
    ),
  [orderedNets]
);

const SpecialNets = useMemo(
  () =>
    orderedNets.filter(
      (item: any) =>
        !item.tool.standard_net
    ),
  [orderedNets]
);

const netPdfHeaders = useMemo(() => {
  const result: any = {
    standard: [],
    special: [],
  };

  const combinedRows: any[] =
    orderedNets.map((net: any) => ({
      rowType: "item",
      item: net,
    }));

  [...(headers?.net || [])]
    .sort(
      (
        firstHeader: any,
        secondHeader: any
      ) =>
        firstHeader.position -
        secondHeader.position
    )
    .forEach((header: any) => {
      const position = Math.max(
        0,
        Math.min(
          header.position,
          combinedRows.length
        )
      );

      combinedRows.splice(position, 0, {
        ...header,
        rowType: "header",
      });
    });

  let standardPosition = 0;
  let specialPosition = 0;
  let previousItem: any = null;

  combinedRows.forEach(
    (row: any, rowIndex: number) => {
      if (row.rowType === "item") {
        previousItem = row.item;

        if (row.item.tool.standard_net) {
          standardPosition += 1;
        } else {
          specialPosition += 1;
        }

        return;
      }

      const nextItemRow =
        combinedRows
          .slice(rowIndex + 1)
          .find(
            (nextRow: any) =>
              nextRow.rowType === "item"
          );

      const associatedItem =
        nextItemRow?.item ||
        previousItem;

      if (!associatedItem) {
        return;
      }

      if (
        associatedItem.tool.standard_net
      ) {
        result.standard.push({
          ...row,
          position: standardPosition,
        });

        standardPosition += 1;
      } else {
        result.special.push({
          ...row,
          position: specialPosition,
        });

        specialPosition += 1;
      }
    }
  );

  return result;
}, [
  orderedNets,
  headers?.net,
]);

    const summaryDetails = {
      rebars: rebars.reduce((sum: any, curr: any) => sum + curr.quantity, 0),
      rebars_weight:
        rebars.reduce(
          (sum: any, i: any) => sum + i.tool.unit_weight * i.quantity,
          0.0
        ) / 1000,
      standard_nets: standardNets.reduce(
        (sum: any, curr: any) => sum + curr.quantity,
        0
      ),
      standard_nets_weight:
        standardNets.reduce(
          (sum: any, i: any) => sum + i.tool.unit_weight * i.quantity,
          0.0
        ) / 1000,
      special_nets: SpecialNets.reduce(
        (sum: any, curr: any) => sum + curr.quantity,
        0
      ),
      special_nets_weight:
        SpecialNets.reduce(
          (sum: any, i: any) => sum + i.tool.unit_weight * i.quantity,
          0.0
        ) / 1000,
      piles: piles.reduce((sum: any, curr: any) => sum + curr.quantity, 0),
      piles_weight:
        piles.reduce(
          (sum: any, i: any) => sum + i.tool.unit_weight * i.quantity,
          0.0
        ) / 1000,
    };

    const rebarPricing = useMemo(
      () =>
        rebars.length > 0
          ? rebars
              .reduce(
                (pre: any, curr: any) => pre + curr.unit_price * curr.quantity,
                0
              )
              .toFixed(2)
          : "0",
      [rebars]
    );

    const calculatePrice = (
      price: number,
      quantity: number,
      isTotalPrice = false,
      isRebars = false,
      isStanNet = false,
      isSpecNet = false
    ) => {
      let basePrice = Number(price);

      const stdFee = (basePrice * Number(bidFees.stdVal)) / 100;
      let easyBarFee = 0;
      //check for the EasyBar FeeType
      if (feeType === 0) {
        easyBarFee =
          ((Number(basePrice) + Number(stdFee)) * Number(bidFees.easyBarFee)) /
          100;
      } else {
        //To Check if we are calculating the easyBar fee of 1 unit or not
        if (quantity === -1) {
          easyBarFee = Number(bidFees.easyBarFee);
        } else {
          easyBarFee = Number(bidFees.easyBarFee) / Number(quantity);
        }
      }

      const payFee =
        ((stdFee + easyBarFee + basePrice) * Number(bidFees.paymentFee)) / 100;

      let totalPrice = stdFee + easyBarFee + payFee + basePrice;
      //Here storing the values in state for the order summary
      if (isTotalPrice) {
        if (isRebars) {
          setRebarsTotalPrice(totalPrice);
        } else if (isStanNet) {
          setStandardNetsTotalPrice(totalPrice);
        } else {
          setSpecialNetsTotalPrice(totalPrice);
        }
      }

      return Number(totalPrice);
    };

    const checkForDecimals = (price: number) => {
      if (price <= 0.5) {
        return Number(price).toFixed(2);
      } else {
        return Number(price).toFixed(1);
      }
    };

    return (
      <>
        <div className="pdf-preview-toolbar" role="toolbar">
          <button
            type="button"
            className="pdf-preview-action pdf-preview-action--primary"
            onClick={() => print(true)}
            disabled={loader || !documentSession}
          >
            {t("download", { ns: "common" })}
          </button>
          <div className="pdf-preview-pagination">
            <button
              type="button"
              className="pdf-preview-action"
              onClick={() => navigatePreview(-1)}
              disabled={!documentSession || visiblePage === 0}
              aria-label={uiT("Previous PDF page")}
            >
              ‹
            </button>
            <span aria-live="polite" dir="ltr" data-pdf-preview-counter>
              {visiblePage + 1} / {pageCount}
            </span>
            <button
              type="button"
              className="pdf-preview-action"
              onClick={() => navigatePreview(1)}
              disabled={!documentSession || visiblePage >= pageCount - 1}
              aria-label={uiT("Next PDF page")}
            >
              ›
            </button>
          </div>
          <button
            type="button"
            className="pdf-preview-action"
            onClick={onHide}
            aria-label={uiT("Close PDF preview")}
          >
            ×
          </button>
        </div>
        <div className="pdf-preview-viewport" ref={previewViewportRef}>
          {documentError && <div role="alert"><p>{uiT("PDF page could not be displayed")}</p><button type="button" className="pdf-preview-action" onClick={() => setDocumentRetry(value => value + 1)}>{uiT("Retry PDF preview")}</button></div>}
          {mobile && !documentError && <MobilePdfPage key={`${i18n.language}:${visiblePage}:${pageCount}`} page={previewPage} capture={getHtmlToCanvas}
            loadingLabel={uiT("Loading PDF page")}
            errorLabel={uiT("PDF page could not be displayed")}
            pageLabel={`${visiblePage + 1} / ${pageCount}`}
            fallbackLabel={t("download", { ns: "common" })} onDownload={() => print(true)} />}

          <div
            aria-hidden={mobile || undefined}
            className={"pdf-preview-stage" + (mobile ? " pdf-preview-capture-source" : "")}
            style={{
              width: mobile ? 940 : 940 * previewScale,
              height: mobile ? "auto" : previewHeight,
            }}
          >
        <div
          id="PDF-Preview-main"
          style={
            loader
              ? {
                  pointerEvents: "none",
                  transform: mobile ? "none" : "scale(" + previewScale + ")",
                }
              : {
                  transform: mobile ? "none" : "scale(" + previewScale + ")",
                }
          }
          ref={stylingRef}
          dir={customRightToLeft ? "rtl" : "ltr"}
          lang={customRightToLeft ? "he" : "en"}
        >
          <PreviewRebarTable
            pageSize={pageSize}
            loader={loader}
            order={order}
            rebars={rebars}
            headers={headers?.rebar || []}
            standardNets={standardNets}
            specialNets={SpecialNets}
            handleRefs={handleRebarsRefs}
            totalPages={totalPages}
            priceShow={priceShow}
            summaryDetails={summaryDetails}
            pdfType={pdfType}
            rebarPricing={rebarPricing}
            bidFees={bidFees}
            rebarsTotalPrice={rebarsTotalPrice}
            calculatePrice={calculatePrice}
            rightToLeft={customRightToLeft}
            checkForDecimals={checkForDecimals}
            showLegacySummary={!usesDedicatedSummary}
          />
          <PreviewStandardNets
            pageSize={pageSize}
            order={order}
            nets={standardNets}
            headers={netPdfHeaders.standard}
            handleRefs={handleStandardNetRefs}
            totalPages={totalPages}
            priceShow={priceShow}
            setStandardNetTran={setStandardNetTran}
            summaryDetails={summaryDetails}
            pdfType={pdfType}
            bidFees={bidFees}
            rebarsTotalPrice={rebarsTotalPrice}
            standardNetsTotalPrice={standardNetsTotalPrice}
            calculatePrice={calculatePrice}
            rightToLeft={customRightToLeft}
            checkForDecimals={checkForDecimals}
            showLegacySummary={!usesDedicatedSummary}
          />
          <PreviewSpecialNets
            pageSize={pageSize}
            order={order}
            nets={SpecialNets}
            headers={netPdfHeaders.special}
            standardNets={standardNets}
            handleRefs={handleSpecialdNetRefs}
            totalPages={totalPages}
            priceShow={priceShow}
            standardNetTran={standardNetTran}
            summaryDetails={summaryDetails}
            pdfType={pdfType}
            rebarPricing={rebarPricing}
            bidFees={bidFees}
            rebarsTotalPrice={rebarsTotalPrice}
            standardNetsTotalPrice={standardNetsTotalPrice}
            specialNetsTotalPrice={specialNetsTotalPrice}
            calculatePrice={calculatePrice}
            rightToLeft={customRightToLeft}
            checkForDecimals={checkForDecimals}
            showLegacySummary={!usesDedicatedSummary}
          />
          <PreviewPileTable
            pageSize={pageSize}
            order={order}
            piles={piles}
            headers={headers?.pile || []}
            handleRefs={hanldePileRefs}
            totalPages={totalPages}
            priceShow={priceShow}
            summaryDetails={summaryDetails}
            pdfType={pdfType}
            rebarsTotalPrice={rebarsTotalPrice}
            standardNetsTotalPrice={standardNetsTotalPrice}
            specialNetsTotalPrice={specialNetsTotalPrice}
            rightToLeft={customRightToLeft}
            checkForDecimals={checkForDecimals}
            showLegacySummary={!usesDedicatedSummary}
          />
          {usesDedicatedSummary && (
            <PdfOrderSummary
              pageSize={pageSize}
              order={order}
              rebars={rebars}
              nets={nets}
              piles={piles}
              handleRefs={handleSummaryRefs}
              totalPages={totalPages}
              rightToLeft={customRightToLeft}
            />
          )}
        </div>
          </div>
        </div>
        {loader && (
          <div className="overloaded-wrapper">
            <div className="overloaded-spinner">
              <Spinner animation="border" role="status">
                <span className="visually-hidden"></span>
              </Spinner>
              <p> {uiT("Generating PDF")} </p>
            </div>
          </div>
        )}
      </>
    );
  }
);

export default PdfPreview;
